var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { AbstractTaskManager, Step } from '@sisense/task-manager';
import { getJaqlQueryPayload, getPivotJaqlQueryPayload } from '../jaql/get-jaql-query-payload.js';
import { QUERY_DEFAULT_LIMIT } from '../query-client.js';
import { getDataFromQueryResult } from '../query-result/index.js';
import { TranslatableError } from '../translation/translatable-error.js';
export class QueryTaskManager extends AbstractTaskManager {
    constructor(queryApi, pivotClient) {
        super();
        /** Map of aborters by task id to be able to cancel sent requests */
        this.sentRequestsAbortersMap = new Map();
        this.executeQuerySending = super.createFlow([
            new Step('PREPARE_JAQL_PAYLOAD', this.prepareJaqlPayload.bind(this), () => __awaiter(this, void 0, void 0, function* () { })),
            new Step('SEND_JAQL_QUERY', this.sendJaqlQuery.bind(this), this.cancelDataRetrievalQuery.bind(this)),
        ]);
        this.executeDownloadCsvSending = super.createFlow([
            new Step('PREPARE_JAQL_PAYLOAD', this.prepareJaqlPayload.bind(this), () => __awaiter(this, void 0, void 0, function* () { })),
            new Step('SEND_DOWNLOAD_CSV_QUERY', this.sendCsvQuery.bind(this), this.cancelDataRetrievalQuery.bind(this)),
        ]);
        this.executePivotQuerySending = super.createFlow([
            new Step('PREPARE_JAQL_PAYLOAD', this.preparePivotJaqlPayload.bind(this), () => __awaiter(this, void 0, void 0, function* () { })),
            new Step('SEND_JAQL_QUERY', this.sendPivotJaqlQuery.bind(this), this.cancelPivotJaqlQuery.bind(this)),
        ]);
        this.queryApi = queryApi;
        this.pivotClient = pivotClient;
    }
    prepareJaqlPayload(task) {
        return __awaiter(this, void 0, void 0, function* () {
            const { queryDescription, executionConfig } = task.passport;
            const jaqlPayload = getJaqlQueryPayload(queryDescription, executionConfig.shouldSkipHighlightsWithoutAttributes);
            const onBeforeQuery = task.passport.executionConfig.onBeforeQuery;
            if (onBeforeQuery) {
                return onBeforeQuery(jaqlPayload);
            }
            return jaqlPayload;
        });
    }
    sendJaqlQuery(task, jaqlPayload) {
        return __awaiter(this, void 0, void 0, function* () {
            const { queryDescription, taskId } = task.passport;
            const { responsePromise, abortHttpRequest } = this.queryApi.sendJaqlRequest(task.passport.queryDescription.dataSource, jaqlPayload);
            this.sentRequestsAbortersMap.set(taskId, abortHttpRequest);
            const jaqlResponse = yield responsePromise.finally(() => {
                this.sentRequestsAbortersMap.delete(taskId);
            });
            validateJaqlResponse(jaqlResponse);
            const metadata = [...queryDescription.attributes, ...queryDescription.measures];
            // extra columns are assumed to have been added by advanced analytics functions
            const extraColumns = (jaqlResponse.headers || [])
                .slice(metadata.length)
                .map((c) => ({ name: c, type: 'number' }));
            return getDataFromQueryResult(jaqlResponse, [...metadata, ...extraColumns]);
        });
    }
    sendCsvQuery(task, jaqlPayload) {
        return __awaiter(this, void 0, void 0, function* () {
            const { taskId } = task.passport;
            const { responsePromise, abortHttpRequest } = this.queryApi.sendDownloadCsvRequest(task.passport.queryDescription.dataSource, jaqlPayload);
            this.sentRequestsAbortersMap.set(taskId, abortHttpRequest);
            return responsePromise.finally(() => {
                this.sentRequestsAbortersMap.delete(taskId);
            });
        });
    }
    cancelDataRetrievalQuery(task) {
        const taskId = task.passport.taskId;
        const abortInitialRequest = this.sentRequestsAbortersMap.get(taskId);
        if (abortInitialRequest) {
            abortInitialRequest();
            this.sentRequestsAbortersMap.delete(taskId);
        }
        return this.queryApi.sendCancelJaqlQueryRequest(taskId, task.passport.queryDescription.dataSource);
    }
    /**
     * Prepares the JAQL payload for the pivot query
     *
     * @param task
     * @returns JAQL payload
     */
    preparePivotJaqlPayload(task) {
        return __awaiter(this, void 0, void 0, function* () {
            const { pivotQueryDescription, executionConfig } = task.passport;
            const jaqlPayload = getPivotJaqlQueryPayload(pivotQueryDescription, executionConfig.shouldSkipHighlightsWithoutAttributes);
            const onBeforeQuery = task.passport.executionConfig.onBeforeQuery;
            if (onBeforeQuery) {
                return onBeforeQuery(jaqlPayload);
            }
            return jaqlPayload;
        });
    }
    /**
     * Executes the pivot query and returns the result
     *
     * @param task
     * @param jaqlPayload
     */
    sendPivotJaqlQuery(task, jaqlPayload) {
        var _a;
        const { pivotQueryDescription } = task.passport;
        return this.pivotClient.queryData(jaqlPayload, true, (_a = pivotQueryDescription.count) !== null && _a !== void 0 ? _a : QUERY_DEFAULT_LIMIT, false);
    }
    cancelPivotJaqlQuery(task) {
        const taskId = task.passport.taskId;
        const abortInitialRequest = this.sentRequestsAbortersMap.get(taskId);
        if (abortInitialRequest) {
            abortInitialRequest();
            this.sentRequestsAbortersMap.delete(taskId);
        }
        return this.queryApi.sendCancelJaqlQueryRequest(taskId, task.passport.pivotQueryDescription.dataSource);
    }
}
export function validateJaqlResponse(jaqlResponse) {
    var _a;
    if (!jaqlResponse) {
        throw new TranslatableError('errors.noJaqlResponse');
    }
    if (jaqlResponse.error) {
        throw new Error(`${jaqlResponse.details} ${(_a = jaqlResponse.database) !== null && _a !== void 0 ? _a : ''} ${jaqlResponse.extraDetails ? JSON.stringify(jaqlResponse.extraDetails) : ''}`);
    }
}
//# sourceMappingURL=query-task-manager.js.map